/**
 * Gen1 order status utilities.
 *
 * Status is treated as a canonical Title Case string across:
 * - Save payload
 * - UI display
 * - UI comparisons / branching
 */

// Canonical values used across the app
export const GEN1_STATUS = Object.freeze({
    DRAFT: 'Draft',
    ORDERED: 'Ordered',
    IN_PROGRESS: 'In Progress',
    COMPLETED: 'Completed',
});

const GEN1_STATUS_META = Object.freeze({
    [GEN1_STATUS.DRAFT]: {
        text: GEN1_STATUS.DRAFT,
        color: 'grey',
        icon: 'pencil',
    },
    [GEN1_STATUS.ORDERED]: {
        text: GEN1_STATUS.ORDERED,
        color: 'blue',
        icon: 'pin',
    },
    [GEN1_STATUS.IN_PROGRESS]: {
        text: GEN1_STATUS.IN_PROGRESS,
        color: 'black',
        icon: 'hourglass half',
    },
    [GEN1_STATUS.COMPLETED]: {
        text: GEN1_STATUS.COMPLETED,
        color: 'green',
        icon: 'check',
    },
});

/**
 * Verify Gen1 status is one of the canonical GEN1_STATUS values.
 */
export function isGen1Status(value) {
    return Object.values(GEN1_STATUS).includes(value);
}

/**
 * Returns canonical status value, otherwise '' (not present).
 * (Kept internal: callers should primarily use GEN1_STATUS + isGen1Status.)
 */
function getGen1StatusOrEmpty(rawStatus) {
    return isGen1Status(rawStatus) ? rawStatus : '';
}

/** Draft のとき true（Save/Delete などの表示条件） */
export function isGen1Draft(rawStatus) {
    return getGen1StatusOrEmpty(rawStatus) === GEN1_STATUS.DRAFT;
}

export const GEN1_EDITABLE_STATUSES = Object.freeze([
    GEN1_STATUS.DRAFT,
    GEN1_STATUS.ORDERED,
    GEN1_STATUS.IN_PROGRESS,
]);

/** Draft / Ordered / In Progress のとき true（編集・Confirm 系の表示条件） */
export function isGen1Editable(rawStatus) {
    const status = getGen1StatusOrEmpty(rawStatus);
    return GEN1_EDITABLE_STATUSES.includes(status);
}

/**
 * Save 成功時に API へ送ったステータスが Draft 以外（Confirm／プレビュー確定）。
 * 完了モーダルでフォーム初期化・申請完了相当の文言に使う。
 */
export function isGen1NonDraftOrderSaveStatus(rawStatus) {
    const code = getGen1StatusOrEmpty(rawStatus);
    return code === GEN1_STATUS.ORDERED || code === GEN1_STATUS.IN_PROGRESS;
}

/**
 * Build props for semantic-ui-react <Label>.
 * Returns { text, color, icon } where icon can be passed as the Label icon prop.
 */
export function getGen1StatusLabelMeta(rawOrCode) {
    const code = getGen1StatusOrEmpty(rawOrCode);
    const meta = GEN1_STATUS_META[code];
    return {
        code,
        text: meta?.text ?? '',
        color: meta?.color ?? 'grey',
        icon: meta?.icon ?? 'question circle',
    };
}

export function getGen1StatusSelectOptions({ includeAll = true } = {}) {
    const opts = Object.values(GEN1_STATUS).map((s) => ({
        key: s,
        value: s,
        text: s,
    }));

    if (!includeAll) return opts;

    return [{ key: '', value: '', text: 'All' }, ...opts];
}
